import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import type {
  ThematicNodeRelation,
  NewThematicNodeRelation,
} from "@/types/thematicMaps";

// DFS: sprawdz czy dodanie relacji parent_of tworzy cykl
async function validateNoParentCycle(
  mapId: string,
  fromNodeId: string,
  toNodeId: string,
): Promise<boolean> {
  const { data: relations } = await (supabase as any)
    .from("thematic_node_relations")
    .select("from_node_id, to_node_id")
    .eq("map_id", mapId)
    .eq("relation_type", "parent_of");

  const adjacency = new Map<string, string[]>();
  for (const r of relations || []) {
    const children = adjacency.get(r.from_node_id) || [];
    children.push(r.to_node_id);
    adjacency.set(r.from_node_id, children);
  }

  // DFS: sprawdz czy z toNodeId mozna dojsc do fromNodeId
  const visited = new Set<string>();
  const stack = [toNodeId];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === fromNodeId) return false; // cykl!
    if (visited.has(current)) continue;
    visited.add(current);
    for (const child of adjacency.get(current) || []) {
      stack.push(child);
    }
  }
  return true; // brak cyklu
}

export const useThematicRelations = (mapId: string | undefined) => {
  const [relations, setRelations] = useState<ThematicNodeRelation[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchRelations = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      // PostgREST caps at 1000 rows per request — fetch in batches via .range()
      const batchSize = 1000;
      let allData: ThematicNodeRelation[] = [];
      let from = 0;

      while (true) {
        const { data, error } = await (supabase as any)
          .from("thematic_node_relations")
          .select("*")
          .eq("map_id", mapId)
          .order("created_at", { ascending: false })
          .range(from, from + batchSize - 1);

        if (error) throw error;
        const batch = (data || []) as ThematicNodeRelation[];
        allData = [...allData, ...batch];
        if (batch.length < batchSize) break;
        from += batchSize;
      }

      setRelations(allData);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Błąd pobierania relacji";
      toast({ title: "Błąd", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createRelation = useCallback(
    async (
      relationData: Omit<NewThematicNodeRelation, "user_id" | "map_id">,
    ): Promise<ThematicNodeRelation | null> => {
      if (!user || !mapId) return null;

      // Walidacja cykli dla parent_of
      if (relationData.relation_type === "parent_of") {
        const noCycle = await validateNoParentCycle(
          mapId,
          relationData.from_node_id,
          relationData.to_node_id,
        );
        if (!noCycle) {
          toast({
            title: "Błąd",
            description: "Relacja tworzy cykl w hierarchii parent_of",
            variant: "destructive",
          });
          return null;
        }
      }

      try {
        const { data, error } = await (supabase as any)
          .from("thematic_node_relations")
          .insert({
            user_id: user.id,
            map_id: mapId,
            ...relationData,
          })
          .select()
          .single();

        if (error) throw error;

        setRelations((prev) => [data as ThematicNodeRelation, ...prev]);
        toast({ title: "Sukces", description: "Relacja dodana" });
        return data as ThematicNodeRelation;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Błąd tworzenia relacji";
        toast({ title: "Błąd", description: msg, variant: "destructive" });
        return null;
      }
    },
    [user, mapId, toast],
  );

  const createRelationsBulk = useCallback(
    async (
      relationsData: Omit<NewThematicNodeRelation, "user_id" | "map_id">[],
    ): Promise<ThematicNodeRelation[]> => {
      if (!user || !mapId) return [];

      try {
        const rows = relationsData.map((r) => ({
          user_id: user.id,
          map_id: mapId,
          ...r,
        }));

        const { data, error } = await (supabase as any)
          .from("thematic_node_relations")
          .insert(rows)
          .select();

        if (error) throw error;

        toast({
          title: "Sukces",
          description: `Dodano ${data.length} relacji`,
        });
        await fetchRelations();
        return data as ThematicNodeRelation[];
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Błąd dodawania relacji";
        toast({ title: "Błąd", description: msg, variant: "destructive" });
        return [];
      }
    },
    [user, mapId, toast, fetchRelations],
  );

  const deleteRelation = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { error } = await (supabase as any)
          .from("thematic_node_relations")
          .delete()
          .eq("id", id);

        if (error) throw error;

        setRelations((prev) => prev.filter((r) => r.id !== id));
        toast({ title: "Sukces", description: "Relacja usunięta" });
        return true;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Błąd usuwania relacji";
        toast({ title: "Błąd", description: msg, variant: "destructive" });
        return false;
      }
    },
    [toast],
  );

  const deleteAllRelations = useCallback(async (): Promise<boolean> => {
    if (!mapId) return false;

    try {
      const { error } = await (supabase as any)
        .from("thematic_node_relations")
        .delete()
        .eq("map_id", mapId);

      if (error) throw error;

      const count = relations.length;
      setRelations([]);
      toast({ title: "Sukces", description: `Usunięto ${count} relacji` });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd usuwania relacji";
      toast({ title: "Błąd", description: msg, variant: "destructive" });
      return false;
    }
  }, [mapId, relations.length, toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchRelations();
    }
  }, [user?.id, mapId, fetchRelations]);

  const { isLive } = useRealtimeSubscription({
    channel: `relations_${mapId || 'none'}`,
    table: 'thematic_node_relations',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: fetchRelations,
  });

  return {
    relations,
    loading,
    isLive,
    createRelation,
    createRelationsBulk,
    deleteRelation,
    deleteAllRelations,
    refreshRelations: fetchRelations,
  };
};
